import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableContainerProps,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    Typography,
    Chip,
    IconButton,
    LinearProgress,
    Box,
    Grid,
    Checkbox,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Alert } from '@mui/material';
import { Check, Clear } from '@mui/icons-material';
import { AI_MAPPING_ENABLED } from '../../../../utils/utils';
import { pageContentGap } from '../../../../theme';

// Confidence thresholds for mapping suggestions
const CONFIDENCE_THRESHOLDS = {
    HIGH: 0.8,
    MEDIUM: 0.6,
    AUTO_SELECT: 0.7
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        maxWidth: "90vw",
        width: "1200px",
        maxHeight: "85vh"
    }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    padding: "16px 24px",
    height: "100%"
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
    padding: "16px 0",
    "& .MuiStepIcon-completed": {
        color: "#22b36e"
    },
    "& .MuiStepIcon-active": {
        color: "#006BFF"
    }
}));

const StyledTable = styled(Table)(({ theme }) => ({
    "& .MuiTableCell-root": {
        fontSize: "0.875rem"
    }
}));

const StyledTableContainer = styled(TableContainer)<TableContainerProps>(({ theme }) => ({
    flexGrow: 1,
    overflow: "auto",
    maxHeight: "400px",
    marginTop: "16px"
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    backgroundColor: "#fafafa"
}));

const SupplierAttribute = styled(Typography)(({ theme }) => ({
    fontWeight: 500
}));

const SuggestedMatch = styled('div')(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px"
}));

const ConfidenceChip = styled(Chip)(({ theme }) => ({
    fontWeight: "bold",
    fontSize: "0.7rem"
}));

const SourceChip = styled(Chip)(({ theme }) => ({
    fontSize: "0.7rem"
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
    padding: "20px 0"
}));

const SummaryBox = styled(Box)(({ theme }) => ({
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px"
}));

const ActionButtons = styled('div')(({ theme }) => ({
    display: "flex",
    gap: "8px"
}));

const BulkActions = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: pageContentGap,
    marginBottom: "12px",
    padding: "8px",
    borderBottom: `1px solid ${theme.palette.divider}`
}));

interface MappingSuggestion {
    supplierAttribute: string;
    retailerAttribute: string;
    confidence: number;
    source: 'Fuzzy' | 'AI' | string;
    [key: string]: unknown;
}

interface MappingReviewDialogProps {
    open: boolean;
    onClose: () => void;
    mappingSuggestions: MappingSuggestion[] | null | undefined;
    onApply: (selected: MappingSuggestion[]) => void;
    isProcessing?: boolean;
    processingStep?: number | null;
    mappingType?: 'attributes' | 'categories' | 'lists' | string;
    isApplying?: boolean;
}

export default function MappingReviewDialog(props: MappingReviewDialogProps) {
    const {
        open,
        onClose,
        mappingSuggestions,
        onApply,
        isProcessing = false,
        processingStep = null,
        mappingType = 'attributes', // 'attributes', 'categories', or 'lists'
        isApplying = false
    } = props;

    const [selectedMappings, setSelectedMappings] = useState({});
    const [selectAll, setSelectAll] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const steps = ['Fuzzy Matching', 'AI Analysis', 'Review Mappings'];

    const getTypeLabel = (plural = false) => {
        switch (mappingType) {
            case 'categories':
                return plural ? 'Categories' : 'Category';
            case 'lists':
                return plural ? 'List Values' : 'List Value';
            default:
                return plural ? 'Attributes' : 'Attribute';
        }
    };

    const getColumnLabels = () => {
        switch (mappingType) {
            case 'categories':
                return { source: 'Your Category', target: 'Retailer Category' };
            case 'lists':
                return { source: 'Your Value', target: 'Retailer Value' };
            default:
                return { source: 'Your Attribute', target: 'Suggested Match' };
        }
    };

    const columnLabels = getColumnLabels();

    useEffect(() => {
        if (open && mappingSuggestions) {
            if (mappingSuggestions.length > 100) {
                // For large datasets, process in chunks to avoid blocking
                const initialSelected = {};
                let index = 0;
                const chunkSize = 100;

                const processChunk = () => {
                    const chunk = mappingSuggestions.slice(index, index + chunkSize);
                    chunk.forEach(suggestion => {
                        initialSelected[suggestion.supplierAttribute] =
                            suggestion.confidence >= CONFIDENCE_THRESHOLDS.AUTO_SELECT;
                    });
                    index += chunkSize;

                    if (index < mappingSuggestions.length) {
                        if (window.requestIdleCallback) {
                            requestIdleCallback(processChunk);
                        } else {
                            setTimeout(processChunk, 0);
                        }
                    } else {
                        setSelectedMappings(initialSelected);
                    }
                };

                processChunk();
            } else {
                // Small dataset, process immediately
                const initialSelected = {};
                mappingSuggestions.forEach(suggestion => {
                    initialSelected[suggestion.supplierAttribute] =
                        suggestion.confidence >= CONFIDENCE_THRESHOLDS.AUTO_SELECT;
                });
                setSelectedMappings(initialSelected);
            }
        }
    }, [open, mappingSuggestions]);

    // Update selectAll checkbox state based on current selections
    useEffect(() => {
        if (mappingSuggestions && mappingSuggestions.length > 0) {
            // For very large datasets, optimize the check
            let allSelected = false;

            if (mappingSuggestions.length > 1000) {
                // For large datasets, check if the count matches
                const selectedCount = Object.values(selectedMappings).filter(Boolean).length;
                allSelected = selectedCount === mappingSuggestions.length;
            } else {
                // For smaller datasets, do the full check
                allSelected = mappingSuggestions.every(
                    suggestion => selectedMappings[suggestion.supplierAttribute] === true
                );
            }

            setSelectAll(allSelected);
        }
    }, [selectedMappings, mappingSuggestions]);

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        // Batch update in a single state change to avoid multiple re-renders
        setSelectedMappings(() => {
            const newSelected = {};
            mappingSuggestions.forEach(suggestion => {
                newSelected[suggestion.supplierAttribute] = newSelectAll;
            });
            return newSelected;
        });
    };

    const handleToggleMapping = (supplierAttribute) => {
        setSelectedMappings(prev => ({
            ...prev,
            [supplierAttribute]: !prev[supplierAttribute]
        }));
    };

    const handleApply = () => {
        // Use double requestAnimationFrame to guarantee UI paint before processing
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // UI is now guaranteed to be updated
                const acceptedMappings = mappingSuggestions.filter(
                    suggestion => selectedMappings[suggestion.supplierAttribute]
                );
                onApply(acceptedMappings);
            });
        });
    };

    const getStatistics = () => {
        if (!mappingSuggestions) return { total: 0, fuzzy: 0, ai: 0, selected: 0, unmapped: 0 };

        const stats = {
            total: mappingSuggestions.length,
            fuzzy: mappingSuggestions.filter(s => s.source === 'Fuzzy').length,
            ai: mappingSuggestions.filter(s => s.source === 'AI').length,
            selected: Object.values(selectedMappings).filter(Boolean).length,
            unmapped: 0  // This will be calculated if we have access to total fields
        };

        return stats;
    };

    const stats = getStatistics();

    const getConfidenceColor = (confidence) => {
        if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return '#22b36e';
        if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return '#006BFF';
        return '#FFB600';
    };

    const getConfidenceLabel = (confidence) => {
        if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return 'High';
        if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return 'Medium';
        return 'Low';
    };

    if (isProcessing) {
        return (
            <StyledDialog open={open} onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            }}>
                <DialogTitle>Processing {getTypeLabel()} Mappings</DialogTitle>
                <StyledDialogContent>
                    <StyledStepper activeStep={processingStep}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </StyledStepper>

                    <ProgressContainer>
                        <Typography variant="body1" gutterBottom align="center">
                            {processingStep === 0 && `Analyzing ${getTypeLabel(true).toLowerCase()} with fuzzy matching...`}
                            {processingStep === 1 && 'Enhancing suggestions with AI...'}
                            {processingStep === 2 && 'Preparing results for review...'}
                        </Typography>
                        <LinearProgress />
                    </ProgressContainer>
                </StyledDialogContent>
            </StyledDialog>
        );
    }

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth={false}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Review {getTypeLabel()} Mappings</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {stats.selected} of {stats.total} mappings selected
                    </Typography>
                </Grid>
            </DialogTitle>
            <StyledDialogContent>
                {isApplying && (
                    <Box p={2} mb={2} style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Typography variant="body1" gutterBottom>
                            Applying mappings, please wait...
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                <SummaryBox>
                    <Grid container spacing={2}>
                        <Grid item size={12}>
                            <Typography variant="body2">
                                Found <strong>{stats.total}</strong> mapping suggestions:
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Chip
                                label={`${stats.fuzzy} Fuzzy Matches`}
                                style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                                size="small"
                            />
                        </Grid>
                        {AI_MAPPING_ENABLED() && stats.ai > 0 && (
                            <Grid item>
                                <Chip
                                    label={`${stats.ai} AI Suggestions`}
                                    style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
                                    size="small"
                                />
                            </Grid>
                        )}
                    </Grid>
                </SummaryBox>

                <BulkActions>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                            // Batch update in a single state change
                            setSelectedMappings(() => {
                                const newSelected = {};
                                mappingSuggestions.forEach(s => {
                                    newSelected[s.supplierAttribute] = true;
                                });
                                return newSelected;
                            });
                        }}
                    >
                        Select All
                    </Button>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                            setSelectedMappings({});
                        }}
                    >
                        Deselect All
                    </Button>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                            // Batch update in a single state change
                            setSelectedMappings(() => {
                                const newSelected = {};
                                mappingSuggestions.forEach(s => {
                                    newSelected[s.supplierAttribute] = s.confidence >= CONFIDENCE_THRESHOLDS.HIGH;
                                });
                                return newSelected;
                            });
                        }}
                    >
                        Select High Confidence
                    </Button>
                </BulkActions>

                {AI_MAPPING_ENABLED() && stats.ai > 0 && (
                    <Alert severity="info" style={{ marginBottom: "12px" }}>
                        AI suggestions are provided for {getTypeLabel(true).toLowerCase()} that couldn't be matched with fuzzy logic.
                        Review carefully before accepting.
                    </Alert>
                )}

                <StyledTableContainer component={Paper}>
                    <StyledTable stickyHeader>
                        <TableHead>
                            <TableRow>
                                <HeaderCell padding="checkbox">
                                    <Checkbox
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        color="primary"
                                    />
                                </HeaderCell>
                                <HeaderCell>{columnLabels.source}</HeaderCell>
                                <HeaderCell>{columnLabels.target}</HeaderCell>
                                <HeaderCell align="center">Confidence</HeaderCell>
                                <HeaderCell align="center">Source</HeaderCell>
                                <HeaderCell align="center">Actions</HeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mappingSuggestions && mappingSuggestions
                                .slice(page * rowsPerPage, (page + 1) * rowsPerPage)  // Only render current page
                                .map((suggestion, index) => (
                                <TableRow
                                    key={page * rowsPerPage + index}  // Adjust key for pagination
                                    selected={selectedMappings[suggestion.supplierAttribute]}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedMappings[suggestion.supplierAttribute] || false}
                                            onChange={() => handleToggleMapping(suggestion.supplierAttribute)}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SupplierAttribute>
                                            {suggestion.supplierAttribute}
                                        </SupplierAttribute>
                                    </TableCell>
                                    <TableCell>
                                        <SuggestedMatch>
                                            <Typography>
                                                {suggestion.retailerAttribute}
                                            </Typography>
                                        </SuggestedMatch>
                                    </TableCell>
                                    <TableCell align="center">
                                        <ConfidenceChip
                                            label={`${Math.round(suggestion.confidence * 100)}% ${getConfidenceLabel(suggestion.confidence)}`}
                                            size="small"
                                            style={{
                                                backgroundColor: `${getConfidenceColor(suggestion.confidence)}20`,
                                                color: getConfidenceColor(suggestion.confidence)
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <SourceChip
                                            label={suggestion.source}
                                            size="small"
                                            variant="outlined"
                                            style={{
                                                color: suggestion.source === 'Fuzzy' ? '#2e7d32' : '#1565c0',
                                                borderColor: suggestion.source === 'Fuzzy' ? '#2e7d32' : '#1565c0'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <ActionButtons>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleToggleMapping(suggestion.supplierAttribute)}
                                                title={selectedMappings[suggestion.supplierAttribute] ? "Reject" : "Accept"}
                                            >
                                                {selectedMappings[suggestion.supplierAttribute] ? <Check /> : <Clear />}
                                            </IconButton>
                                        </ActionButtons>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>

                {mappingSuggestions && mappingSuggestions.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[50, 100, 200]}
                        component="div"
                        count={mappingSuggestions.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}

                {(!mappingSuggestions || mappingSuggestions.length === 0) && (
                    <Box mt={2}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                No mapping suggestions could be found automatically. Please manually map your {getTypeLabel(true).toLowerCase()}.
                            </Typography>
                        </Alert>
                        <Box mt={3} textAlign="center">
                            <Typography variant="body2" color="textSecondary">
                                Close this dialog and use the manual mapping interface to complete the mappings.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </StyledDialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isApplying}>
                    {(!mappingSuggestions || mappingSuggestions.length === 0) ? 'Close' : 'Cancel'}
                </Button>
                {mappingSuggestions && mappingSuggestions.length > 0 && (
                    <Button
                        onClick={handleApply}
                        variant="text"
                        color="primary"
                        disabled={stats.selected === 0 || isApplying}
                        startIcon={isApplying ? <CircularProgress size={16} /> : null}
                    >
                        {isApplying ? 'Applying...' : `Apply ${stats.selected} Mapping${stats.selected !== 1 ? 's' : ''}`}
                    </Button>
                )}
            </DialogActions>
        </StyledDialog>
    );
}
